/**
 * REGA repository — data access for the REGA (Real Estate General Authority)
 * ad-licensing tables: `rega_ad_licenses`, `rega_compliance_logs`,
 * `rega_notifications`.
 *
 * Strangler-Fig per-slice migration (manifest-6, fe-tail T11):
 *   Step 1 (HERE) — consolidated the scattered reads/writes from `useRega`
 *                   and the ads wizard. Query shapes are BYTE-IDENTICAL to
 *                   the inline originals.
 *   Step 2 (flip candidate) — Nest endpoints ALREADY EXIST for the reads
 *                   (`GET /rega/licenses`, `GET /rega/licenses/:id`,
 *                   `GET /rega/notifications`, `POST /rega/compliance` —
 *                   apps/api/src/rega/rega.controller.ts). The flip is
 *                   deferred until response-shape parity with these
 *                   select-strings is verified against a live endpoint
 *                   (zod-parse-at-boundary like the nafath repo); recorded
 *                   as the T11 flip delta.
 *
 * The REGA gov API calls themselves (validate/compliance) stay in
 * `src/services/api.ts` — they were never Supabase.
 */

import { supabase } from "@/integrations/supabase/client";
import type { PostgrestError } from "@supabase/supabase-js";
import type {
  RegaAdLicense,
  RegaComplianceLog,
  RegaNotification,
} from "@/types/rega";

export const regaRepository = {
  /**
   * Patch company_id onto a validated license row (the NestJS validate
   * endpoint doesn't receive companyId, so the FE patches it after save).
   * Fire-and-forget: NEVER throws — a failure logs a console warning exactly
   * as the inline original did.
   */
  async patchLicenseCompanyId(
    adLicenseNumber: string,
    companyId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("rega_ad_licenses")
      .update({ company_id: companyId })
      .eq("ad_license_number", adLicenseNumber);
    if (error)
      console.warn("Failed to set company_id on REGA license:", error.message);
  },

  /**
   * Link a license row to its published listing (and stamp company_id).
   * Returns the error instead of throwing — the wizard toasts a warning and
   * continues (the listing itself is already published).
   */
  async linkLicenseToListing(
    adLicenseNumber: string,
    listingId: string,
    companyId: string | null,
  ): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase
      .from("rega_ad_licenses")
      .update({ listing_id: listingId, company_id: companyId })
      .eq("ad_license_number", adLicenseNumber);
    return { error };
  },

  /** Compliance logs for the company, optionally narrowed to one listing. Throws. */
  async listComplianceLogs(
    companyId: string,
    listingId?: string,
  ): Promise<RegaComplianceLog[]> {
    let query = supabase
      .from("rega_compliance_logs")
      .select("*")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });

    if (listingId) {
      query = query.eq("listing_id", listingId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as unknown as RegaComplianceLog[];
  },

  /**
   * The license row for an ad-license number. Deliberately NOT scoped by
   * company_id: `rega_ad_licenses.company_id` is nullable and only patched
   * after validation completes (see patchLicenseCompanyId) — a company filter
   * would hide legitimately-linked licenses whose company_id hasn't been
   * backfilled, regressing the AdRegaActivity panel. Tenancy is enforced by
   * the `rega_licenses_select_admin` RLS policy (company-scoped, admin-only).
   * company_id-safe hardening tracked in
   * https://github.com/Dallali-App/dallali.com/issues/3065. Throws.
   */
  async getLicenseByAdNumber(
    adLicenseNumber: string,
  ): Promise<RegaAdLicense | null> {
    const { data, error } = await supabase
      .from("rega_ad_licenses")
      .select("*")
      .eq("ad_license_number", adLicenseNumber)
      .maybeSingle();
    if (error) throw error;
    return data as unknown as RegaAdLicense | null;
  },

  /** All license rows of the company, newest first. Throws. */
  async listLicenses(companyId: string): Promise<RegaAdLicense[]> {
    const { data, error } = await supabase
      .from("rega_ad_licenses")
      .select("*")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data || []) as unknown as RegaAdLicense[];
  },

  /** Inbound REGA notifications for the company, newest first. Throws. */
  async listNotifications(companyId: string): Promise<RegaNotification[]> {
    const { data, error } = await supabase
      .from("rega_notifications")
      .select("*")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data || []) as unknown as RegaNotification[];
  },
};
